import { Pipe, PipeTransform } from '@angular/core';
import { Constants } from '../../constants';

/**
 * Single source of truth for "is this task rescheduled" used by the orange status
 * styling on both the detail page (per-task pill) and the list view (rolled-up bullhorn),
 * so both render identically.
 *
 * A task counts as rescheduled when its status is 'R', or it carries a scheduled date or
 * a note (some backends keep the status 'P' but store the reschedule note/date). A
 * completed task ('C') is never treated as rescheduled.
 */
export function isTaskRescheduled(task: any): boolean {
    if (!task || task.status === Constants.TASK_COMPLETED) {
        return false;
    }
    const hasNote = typeof task.note === 'string' && task.note.trim().length > 0;
    return task.status === Constants.TASK_RESCHEDULED || hasNote || !!task.scheduled_date;
}

/** Per-task: true when the given task is rescheduled. Used on detail-page task pills. */
@Pipe({
  standalone: false,
    name: 'isRescheduledTask'
})
export class IsRescheduledTaskPipe implements PipeTransform {
    transform(task: any): boolean {
        return isTaskRescheduled(task);
    }
}

/** Per-list: true when any task in the list is rescheduled. Used on list-view bullhorns. */
@Pipe({
  standalone: false,
    name: 'hasRescheduledTask'
})
export class HasRescheduledTaskPipe implements PipeTransform {
    transform(tasks: any[]): boolean {
        if (!tasks || !Array.isArray(tasks)) {
            return false;
        }
        return tasks.some(task => isTaskRescheduled(task));
    }
}

/** Returns only the rescheduled tasks. Used to render the orange task pill in list views. */
@Pipe({
  standalone: false,
    name: 'rescheduledTasks'
})
export class RescheduledTasksPipe implements PipeTransform {
    transform(tasks: any[]): any[] {
        if (!tasks || !Array.isArray(tasks)) {
            return [];
        }
        return tasks.filter(task => isTaskRescheduled(task));
    }
}
